import React, { useEffect, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Button, Modal, Form, Row, Col, Card } from "react-bootstrap"
import {
    getAllProperties,
    createProperty,
    updateProperty,
    deleteProperty,
    getPropertiesCount
} from "../../actions/commonController"
import PaginationTable from "../../components/paginationTable"

const Properties = () => {
    const [properties, setProperties] = useState([])
    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [currentProperty, setCurrentProperty] = useState(null)
    const [formData, setFormData] = useState({
        name: '',
        developer: '',
        status: 'Offplan',
        completionDate: '',
        price: '',
        location: '',
        propertyType: 'Apartment',
        bedrooms: '',
        area: '',
        description: '',
        maps: '',
        features: []
    })
    const [imageFiles, setImageFiles] = useState([])
    const [totalItems, setTotalItems] = useState(0)
    const [page, setPage] = useState(1)
    const [quantity, setQuantity] = useState(10)
    const [searchTerm, setSearchTerm] = useState('')
    const [initLoad, setInitLoad] = useState(false)

    // Filter states
    const [filters, setFilters] = useState({
        status: 'all',
        developer: 'all',
        completionDate: 'all',
        propertyType: 'all',
        bedrooms: 'all'
    })
    const [availableFilters, setAvailableFilters] = useState({
        developers: [],
        completionDates: [],
        propertyTypes: [],
        bedrooms: []
    })

    const statusOptions = ['Offplan', 'Rent', 'Mortgage', 'Sale', 'Lease', 'Ready Plan', 'Sold Out']
    const propertyTypeOptions = ['Apartment', 'Villa', 'Townhouse', 'Studio', 'Penthouse', 'Residential Plot', 'Residential Floor', 'Residential Building', 'Villa Compound', 'Commercial Building', 'Office Space']
    const bedroomOptions = ['-', 'Studio', '1 BK', '2 BK', '3 BK', '4 BK', '5+ BK']

    const statusColors = {
        'Offplan': 'info',
        'Rent': 'warning',
        'Mortgage': 'secondary',
        'Sale': 'success',
        'Lease': 'primary',
        'Ready Plan': 'dark',
        'Sold Out': 'danger'
    }

    const defaultFeatures = [
        'Swimming Pool',
        'Fitness Center',
        'Spa & Wellness',
        'Parks & Gardens',
        'Community Lounge',
        'Entertainment',
        '24/7 Security',
        'Centrally Air Conditioned',
        'Central Heating',
        'Double Glazed Windows',
        'Balcony & Terrace',
        'Gym & Health Club',
        'BBQ Area',
        'Cafe & Canteen',
        'Laundry Room'
    ]

    useEffect(() => {
        initialization()
    }, [])

    const initialization = async () => {
        try {
            setLoading(true)
            let { status, data } = await getPropertiesCount()

            if (status === "success") {
                setTotalItems(data.total)
                await loadProperties()
            } else {
                console.error("Error fetching properties count:", status)
            }
        } catch (err) {
            console.error("Error in initialization function", err)
        } finally {
            setLoading(false)
        }
    }

    const loadProperties = useCallback(async () => {
        try {
            // Build query parameters for current page
            const queryParams = {
                page: page,
                quantity: quantity
            }

            // Add filter parameters only if they're not 'all'
            if (filters.status !== 'all') queryParams.status = filters.status
            if (filters.developer !== 'all') queryParams.developer = filters.developer
            if (filters.completionDate !== 'all') queryParams.completionDate = filters.completionDate
            if (filters.propertyType !== 'all') queryParams.propertyType = filters.propertyType
            if (filters.bedrooms !== 'all') queryParams.bedrooms = filters.bedrooms
            if (searchTerm) queryParams.search = searchTerm

            setLoading(true)

            // Fetch BOTH: current page data AND all data for filters
            const [propertiesResponse, countResponse, allPropertiesResponse] = await Promise.all([
                getAllProperties(queryParams), // Current page
                getPropertiesCount(queryParams), // Count with filters
                getAllProperties({ quantity: 1000 }) // Fetch ALL for filter options
            ])

            if (propertiesResponse.status === 'success') {
                setProperties(propertiesResponse.data)
                // Use ALL properties for filter options, not just current page
                extractFilterOptions(allPropertiesResponse.data)
            } else {
                toast.error('Failed to load properties')
            }

            if (countResponse.status === 'success') {
                setTotalItems(countResponse.data.total)
            }
        } catch (error) {
            console.error('Error loading properties:', error)
            toast.error('Error loading properties')
        } finally {
            setLoading(false)
        }
    }, [page, quantity, filters, searchTerm])

    // Extract filter options from data
    const extractFilterOptions = (propertiesData) => {
        const developers = [...new Set(propertiesData.map(p => p.developer).filter(Boolean))].sort()
        const completionDates = [...new Set(propertiesData.map(p => p.completionDate).filter(Boolean))].sort()
        const propertyTypes = [...new Set(propertiesData.map(p => p.propertyType).filter(Boolean))].sort()
        // Filter out empty strings for bedrooms
        const bedrooms = [...new Set(propertiesData.map(p => p.bedrooms).filter(bedroom => bedroom && bedroom.trim() !== ''))].sort()

        setAvailableFilters({
            developers,
            completionDates,
            propertyTypes,
            bedrooms
        })
    }

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }))
        setPage(1)
    }

    const clearAllFilters = () => {
        setFilters({
            status: 'all',
            developer: 'all',
            completionDate: 'all',
            propertyType: 'all',
            bedrooms: 'all'
        })
        setSearchTerm('')
        setPage(1)
    }

    const getActiveFilterCount = () => {
        const filterCount = Object.values(filters).filter(value => value !== 'all').length
        return filterCount + (searchTerm ? 1 : 0)
    }

    const handlePageChange = (newPage) => {
        setPage(newPage)
    }

    const handleItemsPerPageChange = (newRowsPerPage) => {
        setQuantity(newRowsPerPage)
        setPage(1)
    }

    const handleSearch = (e) => {
        setSearchTerm(e.target.value)
        setPage(1)
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleFeatureChange = (feature) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.includes(feature)
                ? prev.features.filter(f => f !== feature)
                : [...prev.features, feature]
        }))
    }

    const handleImageChange = (e) => {
        setImageFiles(Array.from(e.target.files))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)

            const formDataToSend = new FormData()

            Object.keys(formData).forEach(key => {
                if (key === 'features') {
                    formDataToSend.append(key, JSON.stringify(formData[key]))
                } else {
                    formDataToSend.append(key, formData[key])
                }
            })

            imageFiles.forEach((file, index) => {
                formDataToSend.append('images', file)
            })

            let response
            if (editMode && currentProperty) {
                formDataToSend.append('id', currentProperty.id)
                response = await updateProperty(formDataToSend)
            } else {
                response = await createProperty(formDataToSend)
            }

            if (response.status === 'success') {
                toast.success(editMode ? 'Property updated successfully' : 'Property created successfully')
                setShowModal(false)
                resetForm()
                loadProperties()
            } else {
                toast.error(response.message || 'Operation failed')
            }
        } catch (error) {
            console.error('Error saving property:', error)
            toast.error('Error saving property')
        } finally {
            setLoading(false)
        }
    }

    const handleEdit = (property) => {
        setCurrentProperty(property)
        setFormData({
            name: property.name || '',
            developer: property.developer || '',
            status: property.status || 'Offplan',
            completionDate: property.completionDate || '',
            price: property.price || '',
            location: property.location || '',
            propertyType: property.propertyType || 'Apartment',
            bedrooms: property.bedrooms || '',
            maps: property.maps || '',
            area: property.area || '',
            description: property.description || '',
            features: property.features || []
        })
        setEditMode(true)
        setShowModal(true)
    }

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this property?')) {
            try {
                setLoading(true)
                const response = await deleteProperty(id)
                if (response.status === 'success') {
                    toast.success('Property deleted successfully')
                    loadProperties()
                } else {
                    toast.error('Failed to delete property')
                }
            } catch (error) {
                console.error('Error deleting property:', error)
                toast.error('Error deleting property')
            } finally {
                setLoading(false)
            }
        }
    }

    const resetForm = () => {
        setFormData({
            name: '',
            developer: '',
            status: 'Offplan',
            completionDate: '',
            price: '',
            location: '',
            propertyType: 'Apartment',
            maps: '',
            bedrooms: '',
            area: '',
            description: '',
            features: []
        })
        setImageFiles([])
        setCurrentProperty(null)
        setEditMode(false)
    }

    const openAddModal = () => {
        resetForm()
        setShowModal(true)
    }

    useEffect(() => {
        if (initLoad) loadProperties()
        setInitLoad(true)
    }, [page, quantity, filters, searchTerm])

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box">
                        <div className="page-title-right">
                            <Button variant="primary" onClick={openAddModal}>
                                <i className="ri-add-line"></i> Add Property
                            </Button>
                        </div>
                        <h4 className="page-title">Properties Management</h4>
                    </div>
                </div>
            </div>

            {/* Advanced Filter Section */}
            <div className="row mb-3">
                <div className="col-12">
                    <Card>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Filters</h6>
                                <div className="d-flex align-items-center gap-2">
                                    {getActiveFilterCount() > 0 && (
                                        <span className="badge bg-primary">{getActiveFilterCount()} active</span>
                                    )}
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={clearAllFilters}
                                        disabled={getActiveFilterCount() === 0}
                                    >
                                        Clear All
                                    </Button>
                                </div>
                            </div>

                            <div className="row g-3">
                                {/* Status Filter */}
                                <div className="col-md-2 col-sm-6">
                                    <Form.Label className="form-label">Status</Form.Label>
                                    <Form.Select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                    >
                                        <option value="all">All Status</option>
                                        {statusOptions.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </Form.Select>
                                </div>

                                {/* Developer Filter */}
                                <div className="col-md-2 col-sm-6">
                                    <Form.Label className="form-label">Developer</Form.Label>
                                    <Form.Select
                                        value={filters.developer}
                                        onChange={(e) => handleFilterChange('developer', e.target.value)}
                                    >
                                        <option value="all">All Developers</option>
                                        {availableFilters.developers.map(developer => (
                                            <option key={developer} value={developer}>{developer}</option>
                                        ))}
                                    </Form.Select>
                                </div>

                                {/* Completion Date Filter */}
                                <div className="col-md-2 col-sm-6">
                                    <Form.Label className="form-label">Completion Date</Form.Label>
                                    <Form.Select
                                        value={filters.completionDate}
                                        onChange={(e) => handleFilterChange('completionDate', e.target.value)}
                                    >
                                        <option value="all">All Dates</option>
                                        {availableFilters.completionDates.map(date => (
                                            <option key={date} value={date}>{date}</option>
                                        ))}
                                    </Form.Select>
                                </div>

                                {/* Property Type Filter */}
                                <div className="col-md-2 col-sm-6">
                                    <Form.Label className="form-label">Property Type</Form.Label>
                                    <Form.Select
                                        value={filters.propertyType}
                                        onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                                    >
                                        <option value="all">All Types</option>
                                        {availableFilters.propertyTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </Form.Select>
                                </div>

                                {/* Bedrooms Filter */}
                                <div className="col-md-2 col-sm-6">
                                    <Form.Label className="form-label">Bedrooms</Form.Label>
                                    <Form.Select
                                        value={filters.bedrooms}
                                        onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                                    >
                                        <option value="all">All Bedrooms</option>
                                        {availableFilters.bedrooms.map(bedroom => (
                                            <option key={bedroom} value={bedroom}>{bedroom}</option>
                                        ))}
                                    </Form.Select>
                                </div>

                                {/* Search Filter */}
                                <div className="col-md-2 col-sm-6">
                                    <Form.Label className="form-label">Search</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search properties..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-centered table-hover table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>ID</th>
                                                <th>Property Name</th>
                                                <th>Developer</th>
                                                <th>Status</th>
                                                <th>Price</th>
                                                <th>Type</th>
                                                <th>Bedrooms</th>
                                                <th>Completion</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {properties.map((property) => (
                                                <tr key={property.id}>
                                                    <td>{property.id}</td>
                                                    <td>{property.name}</td>
                                                    <td>{property.developer}</td>
                                                    <td>
                                                        <span className={`badge bg-${statusColors[property.status] || 'primary'}`}>
                                                            {property.status}
                                                        </span>
                                                    </td>
                                                    <td>{property.price}</td>
                                                    <td>{property.propertyType}</td>
                                                    <td>{property.bedrooms || '-'}</td>
                                                    <td>{property.completionDate || '-'}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-1"
                                                            onClick={() => handleEdit(property)}
                                                        >
                                                            <i className="ri-edit-line"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDelete(property.id)}
                                                        >
                                                            <i className="ri-delete-bin-line"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {properties.length === 0 && (
                                        <div className="text-center py-4">
                                            <p>
                                                No properties found matching your filters.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <PaginationTable
                totalItems={totalItems}
                itemsPerPage={quantity}
                pageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
            />

            {/* Keep your existing Modal code - it remains unchanged */}
            <Modal show={showModal} onHide={() => { setShowModal(false); resetForm() }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editMode ? 'Edit Property' : 'Add New Property'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Property Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Developer *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="developer"
                                        value={formData.developer}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status *</Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        {statusOptions.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Property Type *</Form.Label>
                                    <Form.Select
                                        name="propertyType"
                                        value={formData.propertyType}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        {propertyTypeOptions.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Bedrooms</Form.Label>
                                    <Form.Select
                                        name="bedrooms"
                                        value={formData.bedrooms}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Bedrooms</option>
                                        {bedroomOptions.map(bedroom => (
                                            <option key={bedroom} value={bedroom}>{bedroom}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Price *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 1,990,000"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Area *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="area"
                                        value={formData.area}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 689 sq ft"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Completion Date</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="completionDate"
                                        value={formData.completionDate}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Q4 2026"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Location *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Sheikh Zayed Road, Dubai"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Description *</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        required
                                        style={{ whiteSpace: 'pre-wrap' }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Maps</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="maps"
                                        value={formData.maps}
                                        onChange={handleInputChange}
                                        placeholder="e.g., https://maps.google.com/?q=25.276987,55.296249"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Property Images</Form.Label>
                                    <Form.Control
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Select multiple images for the property gallery
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Features</Form.Label>
                                    <div className="row">
                                        {defaultFeatures.map((feature) => (
                                            <div key={feature} className="col-md-4 col-sm-6">
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`feature-${feature}`}
                                                    label={feature}
                                                    checked={formData.features.includes(feature)}
                                                    onChange={() => handleFeatureChange(feature)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowModal(false); resetForm() }}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Saving...' : editMode ? 'Update Property' : 'Add Property'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    )
}

export default Properties